/**
 * axios setup to use mock service
 */

import axios, { AxiosRequestConfig } from 'axios';

const axiosServices = axios.create({ baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:3010/' });

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('serviceToken');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Renews the access token via the sliding-expiration endpoint and persists it.
 * Single source of truth for "how do we refresh + store a new token" — used by both
 * this interceptor's reactive retry-on-401 and JWTContext's proactive renewal timer.
 */
export async function refreshAccessToken(): Promise<string> {
  const res = await axiosServices.post<{ data: { token: string } }>('/Users/Refresh');
  const newToken = res.data.data.token;
  localStorage.setItem('serviceToken', newToken);
  axiosServices.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  return newToken;
}

function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  const u = url.toLowerCase();
  return u.includes('/users/login') || u.includes('/users/refresh');
}

// Concurrent 401s share a single in-flight refresh call instead of each firing their own.
let refreshPromise: Promise<string> | null = null;

axiosServices.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const responseData = error.response?.data;
    const originalRequest = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;

    const canRetry =
      status === 401 &&
      !!originalRequest &&
      !originalRequest._retried &&
      !isAuthEndpoint(originalRequest.url) &&
      !!localStorage.getItem('serviceToken');

    if (canRetry) {
      originalRequest!._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return axiosServices(originalRequest!);
      } catch {
        // Refresh failed (token genuinely expired or server unreachable) — fall through to redirect.
      }
    }

    if (status === 401 && !window.location.href.includes('/login')) {
      localStorage.removeItem('serviceToken');
      window.location.pathname = '/login';
    }

    // Normalize the rejected error so callers can always check .status and .message
    const normalizedError = {
      ...(typeof responseData === 'object' && responseData !== null ? responseData : {}),
      status,
      message:
        responseData?.message ||
        responseData?.Message ||
        error.message ||
        'Request failed',
      errors: responseData?.errors || responseData?.Errors || [],
    };

    return Promise.reject(normalizedError);
  }
);

export default axiosServices;

export async function fetcher(args: string | [string, AxiosRequestConfig]) {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get(url, { ...config });

  return res.data;
}
